import {
  CreateTransient,
  Button,
  theme as defaultTheme,
} from "@mindlercare/ui-web";
import { useFlag } from "@mindlercare/unleash-client";
import { Typography } from "components/typography";
import CONSTANTS from "core/constants";
import { useImplementedFeature } from "core/selectors/app";
import { format, isValid } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { useMindlerApi } from "api/apiv2";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { FreeCardInfo } from "./FreeCardInfo";
import { InsuranceInfo } from "./InsuranceInfo";
import PatientItem from "./patientItem";
import { PatientToggleIcbtCommunication } from "./patientToggleIcbtCommunication";
import PatientTreatmentStatusModal from "./patientTreatmentStatusModal";
import { PersonalNumber } from "./PersonalNumber";
import { VerificationStatus } from "./medicore/VerificationStatus";
import { VerifyPatient } from "./medicore/VerifyPatient";
import { MedicoreId } from "./medicore/MedicoreId.tsx";

type PatientDataProps = {
  patientUserId?: string | number;
};
export const PatientData = ({ patientUserId }: PatientDataProps) => {
  const { t } = useTranslation();

  const [isEndTreatmentEnabled] = useImplementedFeature("END_TREATMENT");
  const [isTreatmentStatusModalOpen, setTreatmentStatusModalOpen] =
    useState(false);

  const apiv2 = useMindlerApi();
  const { data: patient } = useQuery(
    apiv2.patients.getByUserId.queryOptions(
      {
        patientUserId: Number(patientUserId),
      },
      {
        enabled: !!patientUserId,
      },
    ),
  );

  const isTreatmentActive =
    patient?.treatmentStatus?.lastTreatmentEvent?.type !==
    CONSTANTS.TREATMENT_EVENT_TYPE.TREATMENT_END;

  const icbtCommunicationToggleActive = useFlag(
    "ICBT_COMMUNICATION_PATIENT_TOGGLE",
  );

  if (!patient) {
    return null;
  }

  const phoneNumberFormat = /(\d{2})(\d{2})(\d{3})(\d{2})(\d{2})/;
  const formattedPhoneNumber =
    patient.phoneNumber?.replace(phoneNumberFormat, "$1 $2 $3 $4 $5") ?? "--";
  const patientHasFullAddress =
    typeof patient.userAttributes.address === "string" &&
    typeof patient.userAttributes.city === "string" &&
    typeof patient.userAttributes.postal_code === "string";

  return (
    <>
      <PatientCardContainer data-testid="patientCard">
        <HeaderWrapper>
          <Typography variant="h1" dataTestId="patientName">
            {`${patient.firstName}  ${patient.lastName}`}
          </Typography>
          {isEndTreatmentEnabled && (
            <TreatmentStatusPill $inTreatment={isTreatmentActive}>
              <Button
                testId="treatmentStatusToggleButton"
                variant="neutral"
                onClick={() => setTreatmentStatusModalOpen(true)}
              >
                {isTreatmentActive
                  ? t("IN_TREATMENT")
                  : t("TREATMENT_ENDED_TREATMENT")}
              </Button>
            </TreatmentStatusPill>
          )}
          <VerifyPatient patientUserId={patient.userId} />
        </HeaderWrapper>

        <ItemsWrapper>
          <VerificationStatus patientUserId={patient.userId} />
          <MedicoreId patientUserId={patient.userId} />

          <PersonalNumber personalNumber={patient.nationalIdentity} />

          <PatientItem
            title={t("PHONE_NUMBER")}
            value={formattedPhoneNumber}
            data-testid="Phone number"
          />

          {patient.email && (
            <PatientItem title={t("EMAIL")} value={patient.email} />
          )}

          <PatientItem title={t("USER_ID")} value={patient.userId} />

          {patientHasFullAddress && (
            <>
              <PatientItem
                title={t("ADDRESS")}
                value={patient.userAttributes.address}
              />
              <PatientItem
                title={t("POSTAL_CODE")}
                value={patient.userAttributes.postal_code}
              />
              <PatientItem
                title={t("CITY")}
                value={patient.userAttributes.city}
              />
            </>
          )}
          {patient?.userAttributes.date_of_birth &&
            isValid(new Date(patient?.userAttributes.date_of_birth ?? "")) && (
              <PatientItem
                title={t("DATE_OF_BIRTH")}
                value={format(
                  new Date(patient.userAttributes.date_of_birth),
                  CONSTANTS.DATE_FORMAT.DEFAULT_DATE_FORMAT,
                )}
              />
            )}

          <FreeCardInfo patient={patient} />

          <InsuranceInfo patientUserId={patient.userId} />

          {icbtCommunicationToggleActive && (
            <PatientToggleIcbtCommunication patientUserId={patient.userId} />
          )}
        </ItemsWrapper>
      </PatientCardContainer>
      <PatientTreatmentStatusModal
        open={isTreatmentStatusModalOpen}
        onClose={() => setTreatmentStatusModalOpen(false)}
        patientUserId={patient.userId}
      />
    </>
  );
};

const PatientCardContainer = styled.div`
  padding: 34px 24px 32px 0;
`;

const TreatmentStatusPill = styled.div<
  CreateTransient<{ inTreatment: boolean }>
>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 20px;
  margin: 0 16px;
  height: 36px;
  border-radius: 70px;
  background-color: ${({ $inTreatment, theme }) =>
    $inTreatment
      ? theme.COLORS.TERTIARY_GREEN
      : theme.COLORS.TERTIARY_LIGHT_RED};
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;

  & :nth-child(3) {
    margin-left: auto;
  }
`;

const ItemsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${defaultTheme.PADDING.SM};
  margin-top: ${(props) => props.theme.SPACING.LG};
`;
